import { useState, useCallback, useRef } from 'react';

interface UseQuickSaleModalsProps {
  clearCart: () => void;
}

interface UseQuickSaleModalsReturn {
  // Alert Modal
  alertModalOpen: boolean;
  alertModalTitle: string;
  alertModalMessage: string;
  isCriticalError: boolean;
  showQuickAddButton: boolean;
  alertModalRetryCallback: (() => void) | null;
  showAlertModal: (title: string, message: string, showQuickAdd?: boolean, onRetry?: (() => void) | null, onDismiss?: (() => void) | null) => void;
  showCriticalErrorModal: (title: string, message: string) => void;
  handleCloseAlertModal: () => void;
  handleCriticalErrorClearCart: () => void;
  
  // Quick Add Item Modal
  isQuickAddItemModalOpen: boolean;
  setIsQuickAddItemModalOpen: (open: boolean) => void;
  handleOpenQuickAddItemModal: () => void;
  handleCloseQuickAddItemModal: () => void;
  
  // Bulk Add Account Modal
  isBulkAddAccountModalOpen: boolean;
  setIsBulkAddAccountModalOpen: (open: boolean) => void;
  handleOpenBulkAddAccountModal: () => void;
  handleCloseBulkAddAccountModal: () => void;
  
  // Partial Payment Modal
  isPartialPaymentModalOpen: boolean;
  setIsPartialPaymentModalOpen: (open: boolean) => void;
  handleOpenPartialPaymentModal: () => void;
  handleClosePartialPaymentModal: () => void;
}

export const useQuickSaleModals = ({
  clearCart
}: UseQuickSaleModalsProps): UseQuickSaleModalsReturn => {
  // Alert Modal State'leri
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertModalTitle, setAlertModalTitle] = useState('');
  const [alertModalMessage, setAlertModalMessage] = useState('');
  const [isCriticalError, setIsCriticalError] = useState(false);
  const [showQuickAddButton, setShowQuickAddButton] = useState(false);
  const [alertModalRetryCallback, setAlertModalRetryCallback] = useState<(() => void) | null>(null);

  // Quick Add Item Modal State
  const [isQuickAddItemModalOpen, setIsQuickAddItemModalOpen] = useState(false);

  // Bulk Add Account Modal State
  const [isBulkAddAccountModalOpen, setIsBulkAddAccountModalOpen] = useState(false);

  // Partial Payment Modal State
  const [isPartialPaymentModalOpen, setIsPartialPaymentModalOpen] = useState(false);

  // ── "Yeniden Dene" seçilmeden kapatma bildirimi (kayıt hatası telemetrisi) ──
  // QuickSaleModals'ta "Yeniden Dene" önce pencereyi kapatır (handleCloseAlertModal),
  // sonra retry'ı çağırır. Bu yüzden kapatma bildirimi bir tick ertelenir; aynı
  // tıklamada retry çağrıldıysa bildirim yapılmaz.
  const alertDismissRef = useRef<(() => void) | null>(null);
  const lastAlertCloseRef = useRef<{ retried: boolean } | null>(null);

  // Alert Modal Fonksiyonları
  const showAlertModal = useCallback((title: string, message: string, showQuickAdd: boolean = false, onRetry?: (() => void) | null, onDismiss?: (() => void) | null) => {
    // Kapatılmadan üzerine başka pencere açılan kayıt hatası penceresi: kasiyer karar vermedi → kapatıldı say
    const replacedDismiss = alertDismissRef.current;
    alertDismissRef.current = onDismiss ?? null;
    if (replacedDismiss) {
      try { replacedDismiss(); } catch { /* yoksay */ }
    }
    setAlertModalTitle(title);
    setAlertModalMessage(message);
    setAlertModalOpen(true);
    setIsCriticalError(false);
    setShowQuickAddButton(showQuickAdd);
    setAlertModalRetryCallback(onRetry ? () => () => {
      if (lastAlertCloseRef.current) lastAlertCloseRef.current.retried = true;
      onRetry();
    } : null);
  }, []);

  const showCriticalErrorModal = useCallback((title: string, message: string) => {
    setAlertModalTitle(title);
    setAlertModalMessage(message);
    setAlertModalOpen(true);
    setIsCriticalError(true);
  }, []);

  const handleCloseAlertModal = useCallback(() => {
    setAlertModalOpen(false);
    setAlertModalTitle('');
    setAlertModalMessage('');
    setIsCriticalError(false);
    setShowQuickAddButton(false);
    setAlertModalRetryCallback(null);

    const dismiss = alertDismissRef.current;
    alertDismissRef.current = null;
    if (dismiss) {
      const closing = { retried: false };
      lastAlertCloseRef.current = closing;
      setTimeout(() => {
        if (!closing.retried) {
          try { dismiss(); } catch { /* yoksay */ }
        }
      }, 0);
    }
  }, []);

  const handleCriticalErrorClearCart = useCallback(() => {
    clearCart();
    handleCloseAlertModal();
  }, [clearCart, handleCloseAlertModal]);

  // Quick Add Item Modal Fonksiyonları
  const handleOpenQuickAddItemModal = useCallback(() => {
    setIsQuickAddItemModalOpen(true);
  }, []);

  const handleCloseQuickAddItemModal = useCallback(() => {
    setIsQuickAddItemModalOpen(false);
  }, []);

  // Bulk Add Account Modal Fonksiyonları
  const handleOpenBulkAddAccountModal = useCallback(() => {
    setIsBulkAddAccountModalOpen(true);
  }, []);

  const handleCloseBulkAddAccountModal = useCallback(() => {
    setIsBulkAddAccountModalOpen(false);
  }, []);

  // Partial Payment Modal Fonksiyonları
  const handleOpenPartialPaymentModal = useCallback(() => {
    setIsPartialPaymentModalOpen(true);
  }, []);

  const handleClosePartialPaymentModal = useCallback(() => {
    setIsPartialPaymentModalOpen(false);
  }, []);

  return {
    // Alert Modal
    alertModalOpen,
    alertModalTitle,
    alertModalMessage,
    isCriticalError,
    showQuickAddButton,
    alertModalRetryCallback,
    showAlertModal,
    showCriticalErrorModal,
    handleCloseAlertModal,
    handleCriticalErrorClearCart,
    
    // Quick Add Item Modal
    isQuickAddItemModalOpen,
    setIsQuickAddItemModalOpen,
    handleOpenQuickAddItemModal,
    handleCloseQuickAddItemModal,
    
    // Bulk Add Account Modal
    isBulkAddAccountModalOpen,
    setIsBulkAddAccountModalOpen,
    handleOpenBulkAddAccountModal,
    handleCloseBulkAddAccountModal,
    
    // Partial Payment Modal
    isPartialPaymentModalOpen,
    setIsPartialPaymentModalOpen,
    handleOpenPartialPaymentModal,
    handleClosePartialPaymentModal,
  };
};

